import { SimpleFormProps } from './SimpleForm.types';
import { SimpleFormButtons } from './SimpleFormButtons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Grid } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AnyObjectSchema } from 'yup';

export const SimpleForm = <S extends AnyObjectSchema, T extends object>({
  validationSchema,
  validationMode = 'onBlur',
  isEditable = true,
  onSubmit,
  onDelete,
  deleteButtonText,
  onGoBack,
  onCancel,
  isLoading = false,
  spacing = 3,
  defaultValues,
  deleteConfirmationText,
  showButtons = true,
  fieldErrors,
  dialogProps,
  children,
}: SimpleFormProps<S, T>) => {
  const { handleSubmit, control, formState, watch, reset, setError, setValue, ...rest } = useForm({
    mode: validationMode,
    resolver: yupResolver(validationSchema),
    defaultValues,
  });
  const { t } = useTranslation();

  const isFormEditable = !isLoading && isEditable;

  useEffect(() => {
    if (fieldErrors && typeof fieldErrors === 'object') {
      Object.entries(fieldErrors).forEach(([field, errorMessage]) => {
        setError(field, {
          type: 'manual',
          message: Array.isArray(errorMessage) ? errorMessage.join(', ') : (errorMessage as unknown as string),
        });
      });
    }
  }, [fieldErrors, setError]);

  const trySubmit = async (formData: T) => {
    try {
      await onSubmit(formData);
      reset(formData);
    } catch (error) {
      //throw again so that the async errors are handled in the querying hooks
      throw error;
    }
  };

  return (
    <form onSubmit={handleSubmit(trySubmit)}>
      {!isEditable && (
        <Alert sx={{ mb: spacing, mt: -2 }} severity="warning">
          {t('components:form.noEdit')}
        </Alert>
      )}
      <Grid container spacing={spacing}>
        {children({
          handleSubmit,
          control,
          formState,
          watch,
          reset,
          setError,
          setValue,
          isFormEditable,
          ...rest,
        })}
        {!!showButtons && (
          <SimpleFormButtons<T>
            onDelete={onDelete}
            onCancel={onGoBack || onCancel}
            deleteButtonText={deleteButtonText}
            deleteConfirmationText={deleteConfirmationText}
            formState={formState}
            isLoading={isLoading}
            isFormEditable={isFormEditable}
            spacing={spacing}
            dialogProps={dialogProps}
          />
        )}
      </Grid>
    </form>
  );
};
